import { Card, Divider, Layout, Tag, Text } from '@loadsmart/miranda-react'

import { type HandlingUnit as HandlingUnitType } from '../domain/HandlingUnit'
import {
  formatFreightClass,
  formatHandlingUnitDimensionsSummary,
  formatHandlingUnitTitle,
  formatPOandSONumbers,
} from '../presenters/HandlingUnitPresenter'
import { SummaryLabel } from './SummaryLabel'

type HandlingUnitProps = {
  index: number
  handlingUnit: HandlingUnitType
  children: React.ReactNode
  showOrderNumbers?: boolean
}

export function HandlingUnit({
  index,
  handlingUnit,
  children,
  showOrderNumbers = true,
}: HandlingUnitProps) {
  return (
    <Layout.Stack gap="none" key={handlingUnit.uuid}>
      <Card aria-label={`Handling unit #${index}`} collapsible>
        <Card.Title>
          <Layout.Stack gap="spacing-2">
            <Layout.Group>
              {handlingUnit.isHazmat && (
                <Tag size="small" variant="warning">
                  Hazmat
                </Tag>
              )}
              {handlingUnit.isStackable && (
                <Tag size="small" variant="neutral">
                  Stackable
                </Tag>
              )}
              {handlingUnit.isTurnable && (
                <Tag size="small" variant="neutral">
                  Turnable
                </Tag>
              )}
            </Layout.Group>
            <Text variant="heading-sm-bold">{formatHandlingUnitTitle(handlingUnit)}</Text>
            <Layout.Stack gap="spacing-2">
              <SummaryLabel
                inline
                data-testid="weight-summary"
                value={`Weighing ${handlingUnit.totalWeight} in total`}
              />
              <Layout.Group gap="spacing-4">
                <SummaryLabel
                  inline
                  label="Dimensions"
                  data-testid="dimensions-summary"
                  value={formatHandlingUnitDimensionsSummary(handlingUnit)}
                />
                <SummaryLabel
                  inline
                  label="Freight class"
                  data-testid="freight_class-summary"
                  value={formatFreightClass(handlingUnit.freightClass)}
                />
                {showOrderNumbers && (
                  <>
                    <SummaryLabel
                      inline
                      label="PO number"
                      data-testid="po_number-summary"
                      value={formatPOandSONumbers(handlingUnit.poNumber)}
                    />
                    <SummaryLabel
                      inline
                      label="SO number"
                      data-testid="so_number-summary"
                      value={formatPOandSONumbers(handlingUnit.soNumber)}
                    />
                  </>
                )}
              </Layout.Group>
            </Layout.Stack>
          </Layout.Stack>
        </Card.Title>
        <Card.Body>{children}</Card.Body>
      </Card>
      <Divider />
    </Layout.Stack>
  )
}
